import React, { useState, useEffect } from 'react';
import { X, Calendar, MapPin, DollarSign, Clock, Users, Shield, Plus, Edit, Loader2, CalendarPlus, Heart, Building2, PartyPopper, Home, MoreHorizontal, FileText, XCircle } from 'lucide-react';
import axios from 'axios';
import { loadGoogleMapsScript } from '@/utils/googleMapsLoader';
import { initPlaceAutocomplete, destroyPlaceAutocomplete } from '@/utils/placeAutocomplete';
import { toast } from 'sonner';
import CancelCrewMemberModal from '@/components/CancelCrewMemberModal';
import { getAdminEmail } from '@/utils/adminAuth';

// ownerMode: the business owner editing their own job from Hire Crew. Saves through the
// owner endpoint (PUT /api/jobs/{id}) and hides admin-only parts. Admin pages leave it off.
const AdvancedEditJobModal = ({ job, isOpen, onClose, onUpdate, isDark = false, ownerMode = false }) => {
  const [loading, setLoading] = useState(false);
  const [locationSelected, setLocationSelected] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const adminEmail = getAdminEmail();
  
  const [formData, setFormData] = useState({
    title: '',
    dates: [''],
    location: '',
    description: '',
    crewCount: 1,
    crewMembers: [
      { 
        role: '', 
        id: 1,
        dateAssignments: [
          { dateIndex: 0, hours: '', ratePerHour: '' }
        ]
      }
    ],
    requiresWWCC: false,
    jobType: '',
    adminNotes: ''
  });

  const [errors, setErrors] = useState({});

  // Normalize role values to match PostJob lowercase format
  const normalizeRole = (role) => {
    if (!role) return '';
    const lower = role.toLowerCase().replace(/\s+/g, '_');
    const roleMap = {
      'video_editor': 'editor_video',
      'photo_editor': 'editor_photos',
      'photographer': 'photographer',
      'videographer': 'videographer',
      'assistant': 'assistant',
      'drone_operator': 'drone_operator',
      'editor_video': 'editor_video',
      'editor_photos': 'editor_photos',
    };
    return roleMap[lower] || lower;
  };

  // Helper function to format date as DD MMM YYYY for display (e.g., "20 Dec 2025")
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const day = String(date.getDate()).padStart(2, '0');
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch {
      return dateStr;
    }
  };

  // Initialize form data when job is loaded
  useEffect(() => {
    if (job && isOpen) {
      console.log('📋 Loading job for edit:', job);

      // Extract unique dates from crew assignments or job dates
      const getAllJobDates = () => {
        // First try to get dates from crew member assignments
        const crewData = job.crewMembers || job.crew_members || [];
        if (crewData.length > 0 && crewData.some(m => m.dateAssignments && m.dateAssignments.length > 0)) {
          const allDates = new Set();
          crewData.forEach(member => {
            if (member.dateAssignments) {
              member.dateAssignments.forEach(assignment => {
                if (assignment.date) {
                  // Normalize date format to YYYY-MM-DD
                  const dateStr = assignment.date.split('T')[0];
                  allDates.add(dateStr);
                }
              });
            }
          });
          if (allDates.size > 0) {
            return Array.from(allDates).sort();
          }
        }

        // Fall back to job dates or additional_dates
        if (job.additional_dates && job.additional_dates.length > 0) {
          const dates = [job.date, ...job.additional_dates].filter(Boolean);
          return dates.map(d => d.split('T')[0]);
        }

        return job.dates || (job.date ? [job.date.split('T')[0]] : ['']);
      };

      const jobDates = getAllJobDates();
      console.log('📅 Job dates extracted:', jobDates);

      const crewMembers = job.crewMembers || job.crew_members || [];
      console.log('👥 Crew members from job:', crewMembers);

      // Map crew members with proper dateAssignments conversion
      const mappedCrewMembers = crewMembers.length > 0 ? crewMembers.map((member, idx) => {
        console.log(`  Crew member ${idx + 1}:`, member);

        let assignments = [];

        // Check if member has dateAssignments with date strings
        if (member.dateAssignments && member.dateAssignments.length > 0) {
          // Convert date-based assignments to index-based
          assignments = member.dateAssignments.map(assignment => {
            const assignmentDate = assignment.date ? assignment.date.split('T')[0] : '';
            const dateIndex = jobDates.findIndex(d => d === assignmentDate);
            return {
              dateIndex: dateIndex >= 0 ? dateIndex : 0,
              hours: assignment.hours ? assignment.hours.toString() : '',
              ratePerHour: assignment.ratePerHour ? assignment.ratePerHour.toString() : ''
            };
          });
        }

        // If no assignments but has hours/rate at member level, distribute across dates
        if (assignments.length === 0 && (member.hours || member.ratePerHour)) {
          const totalHours = parseFloat(member.hours) || 0;
          const rate = parseFloat(member.ratePerHour) || 0;
          const hoursPerDate = jobDates.length > 0 ? Math.round(totalHours / jobDates.length) : totalHours;

          assignments = jobDates.map((date, dateIdx) => ({
            dateIndex: dateIdx,
            hours: hoursPerDate.toString(),
            ratePerHour: rate.toString()
          }));
        }

        // Ensure we have at least one assignment for each date
        if (assignments.length === 0) {
          assignments = jobDates.map((date, dateIdx) => ({
            dateIndex: dateIdx,
            hours: '',
            ratePerHour: ''
          }));
        }

        console.log(`  Mapped assignments for ${member.role}:`, assignments);

        const normalizedRole = normalizeRole(member.role);
        return {
          role: normalizedRole,
          id: idx + 1,
          dateAssignments: assignments,
          // Preserve hire metadata so admins can take per-crew actions (e.g. no-show cancel)
          hired: member.hired === true,
          hired_user_id: member.hired_user_id || null,
          hired_name: member.hired_name || null,
          hired_email: member.hired_email || null,
          hired_photo: member.hired_photo || null,
          payment_released: member.payment_released === true,
          subtotal: member.subtotal || ((parseFloat(member.hours) || 0) * (parseFloat(member.ratePerHour) || 0))
        };
      }) : [{
        role: '',
        id: 1,
        dateAssignments: jobDates.map((date, dateIdx) => ({
          dateIndex: dateIdx,
          hours: '',
          ratePerHour: ''
        }))
      }];

      console.log('✅ Mapped crew members:', mappedCrewMembers);

      setFormData({
        title: job.title || '',
        dates: jobDates.length > 0 ? jobDates : [''],
        location: job.location || job.location_address || '',
        description: job.description || job.brief || '',
        crewCount: job.crewCount || job.crew_count || crewMembers.length || 1,
        crewMembers: mappedCrewMembers,
        requiresWWCC: job.requiresWWCC || job.requires_wwcc || false,
        jobType: job.job_type || job.jobType || '',
        adminNotes: job.admin_notes || job.notes || ''
      });

      if (job.location) {
        setLocationSelected(true);
      }
    }
  }, [job, isOpen]);

  // Initialize Google Places when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        loadGoogleMapsScript(() => {
          setTimeout(() => {
            try {
              initPlaceAutocomplete(
                'edit-job-location-autocomplete',
                (place) => {
                  if (place && place.formatted_address) {
                    setFormData(prev => ({ ...prev, location: place.formatted_address }));
                    setLocationSelected(true);
                    if (place.lat && place.lng) {
                      setSelectedLocation({ lat: place.lat, lng: place.lng });
                    }
                  }
                }
              );
            } catch (error) {
              console.error('Error initializing autocomplete:', error);
            }
          }, 1000);
        });
      }, 500);
    }

    return () => {
      try {
        destroyPlaceAutocomplete('edit-job-location-autocomplete');
      } catch (e) {}
    };
  }, [isOpen]);

  const addDate = () => {
    const newDateIndex = formData.dates.length;
    setFormData(prev => ({
      ...prev,
      dates: [...prev.dates, ''],
      crewMembers: prev.crewMembers.map(member => ({
        ...member,
        dateAssignments: [
          ...member.dateAssignments,
          { dateIndex: newDateIndex, hours: '', ratePerHour: '' }
        ]
      }))
    }));
  };

  const removeDate = (index) => {
    if (formData.dates.length > 1) {
      setFormData(prev => ({
        ...prev,
        dates: prev.dates.filter((_, i) => i !== index),
        crewMembers: prev.crewMembers.map(member => ({
          ...member,
          dateAssignments: member.dateAssignments
            .filter(a => a.dateIndex !== index)
            .map(a => ({
              ...a,
              dateIndex: a.dateIndex > index ? a.dateIndex - 1 : a.dateIndex
            }))
        }))
      }));
    }
  };

  const updateDate = (index, value) => {
    setFormData(prev => ({
      ...prev,
      dates: prev.dates.map((date, i) => i === index ? value : date)
    }));
  };

  const handleCrewCountChange = (count) => {
    const newCount = parseInt(count) || 1;
    const currentMembers = formData.crewMembers;
    
    if (newCount > currentMembers.length) {
      const newMembers = [...currentMembers];
      for (let i = currentMembers.length; i < newCount; i++) {
        newMembers.push({ 
          role: '', 
          id: i + 1,
          dateAssignments: formData.dates.map((_, dateIndex) => ({
            dateIndex,
            hours: '',
            ratePerHour: ''
          }))
        });
      }
      setFormData(prev => ({
        ...prev,
        crewCount: newCount,
        crewMembers: newMembers
      }));
    } else if (newCount < currentMembers.length) {
      setFormData(prev => ({
        ...prev,
        crewCount: newCount,
        crewMembers: currentMembers.slice(0, newCount)
      }));
    } else {
      setFormData(prev => ({ ...prev, crewCount: newCount }));
    }
  };

  const handleDateAssignmentChange = (memberIndex, dateIndex, field, value) => {
    const newMembers = [...formData.crewMembers];
    const member = { ...newMembers[memberIndex] };
    const newAssignments = [...member.dateAssignments];
    
    let assignmentIndex = newAssignments.findIndex(a => a.dateIndex === dateIndex);
    if (assignmentIndex === -1) {
      newAssignments.push({ dateIndex, hours: '', ratePerHour: '' });
      assignmentIndex = newAssignments.length - 1;
    }
    
    newAssignments[assignmentIndex] = {
      ...newAssignments[assignmentIndex],
      [field]: value
    };
    
    member.dateAssignments = newAssignments;
    newMembers[memberIndex] = member;
    
    setFormData(prev => ({
      ...prev,
      crewMembers: newMembers
    }));
  };

  const removeDateAssignmentForMember = (memberIndex, dateIndex) => {
    setFormData(prev => {
      const newMembers = [...prev.crewMembers];
      const member = { ...newMembers[memberIndex] };
      member.dateAssignments = member.dateAssignments.filter(a => a.dateIndex !== dateIndex);
      newMembers[memberIndex] = member;
      return { ...prev, crewMembers: newMembers };
    });
  };

  const addDateAssignmentForMember = (memberIndex, dateIndex) => {
    setFormData(prev => {
      const newMembers = [...prev.crewMembers];
      const member = { ...newMembers[memberIndex] };
      member.dateAssignments = [...member.dateAssignments, { dateIndex, hours: '', ratePerHour: '' }];
      newMembers[memberIndex] = member;
      return { ...prev, crewMembers: newMembers };
    });
  };

  const handleCrewMemberChange = (memberIndex, field, value) => {
    const newMembers = [...formData.crewMembers];
    newMembers[memberIndex] = { ...newMembers[memberIndex], [field]: value };
    setFormData(prev => ({
      ...prev,
      crewMembers: newMembers
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) newErrors.title = 'Job title is required';
    const validDates = formData.dates.filter(d => d.trim());
    if (validDates.length === 0) newErrors.dates = 'At least one date is required';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    
    const invalidCrew = formData.crewMembers.filter(member => {
      if (!member.role) return true;
      
      const hasValidAssignment = member.dateAssignments.some(assignment => 
        assignment.hours && parseFloat(assignment.hours) > 0 &&
        assignment.ratePerHour && parseFloat(assignment.ratePerHour) > 0
      );
      
      return !hasValidAssignment;
    });
    
    if (invalidCrew.length > 0) {
      newErrors.crewMembers = `All crew members must have a role and at least one date with hours and rate assigned`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const validDates = formData.dates.filter(d => d.trim());
      
      // Calculate total budget
      const totalBudget = formData.crewMembers.reduce((total, member) => {
        const memberTotal = member.dateAssignments.reduce((sum, assignment) => {
          const hours = parseFloat(assignment.hours) || 0;
          const rate = parseFloat(assignment.ratePerHour) || 0;
          return sum + (hours * rate);
        }, 0);
        return total + memberTotal;
      }, 0);

      // Build required roles object
      const required_roles = {};
      formData.crewMembers.forEach(member => {
        if (member.role) {
          required_roles[member.role] = true;
        }
      });

      const jobData = {
        title: formData.title,
        date: validDates[0],
        additional_dates: validDates.slice(1),
        location: formData.location,
        latitude: selectedLocation?.lat || null,
        longitude: selectedLocation?.lng || null,
        budget: totalBudget,
        description: formData.description,
        job_type: formData.jobType,
        ...(ownerMode ? {} : { notes: formData.adminNotes }),
        crewCount: formData.crewCount,
        crewMembers: formData.crewMembers.map(member => {
          const totalHours = member.dateAssignments.reduce((sum, a) => sum + (parseFloat(a.hours) || 0), 0);
          const activeEntries = member.dateAssignments.filter(a => (parseFloat(a.hours) || 0) > 0 && (parseFloat(a.ratePerHour) || 0) > 0);
          const avgRate = activeEntries.length > 0
            ? activeEntries.reduce((sum, a) => sum + (parseFloat(a.ratePerHour) || 0), 0) / activeEntries.length
            : 0;
          
          return {
            role: member.role,
            hours: totalHours,
            ratePerHour: avgRate,
            subtotal: member.dateAssignments.reduce((sum, a) => {
              return sum + ((parseFloat(a.hours) || 0) * (parseFloat(a.ratePerHour) || 0));
            }, 0),
            dateAssignments: member.dateAssignments.map(a => ({
              date: validDates[a.dateIndex],
              hours: parseFloat(a.hours) || 0,
              ratePerHour: parseFloat(a.ratePerHour) || 0,
              subtotal: (parseFloat(a.hours) || 0) * (parseFloat(a.ratePerHour) || 0)
            }))
          };
        }),
        required_roles: required_roles,
        requiresWWCC: formData.requiresWWCC
      };

      console.log('📤 Updating advanced job:', jobData);

      // Owners must not use /api/admin/...: the admin guard rejects them and the admin
      // interceptor used to send them to the admin login.
      const response = ownerMode
        ? await axios.put(
            `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${job.id}`,
            jobData,
            { headers: { Authorization: `Bearer ${localStorage.getItem('shootzu_token')}` } }
          )
        : await axios.put(
            `${process.env.REACT_APP_BACKEND_URL}/api/admin/jobs/${job.id}`,
            jobData
          );

      console.log('✅ Job updated successfully:', response.data);
      toast.success('Job updated successfully!');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('❌ Error updating job:', error);
      toast.error(error.response?.data?.detail || 'Failed to update job');
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = [
    { value: 'photographer', label: 'Photographer' },
    { value: 'videographer', label: 'Videographer' },
    { value: 'editor_video', label: 'Video Editor' },
    { value: 'editor_photos', label: 'Photo Editor' },
    { value: 'assistant', label: 'Assistant' },
    { value: 'drone_operator', label: 'Drone Operator' },
    { value: 'voxpop_videographer', label: 'VoxPop Videographer' }
  ];

  const jobTypeOptions = [
    { value: 'WEDDINGS', label: 'Wedding', icon: Heart, color: '#ec4899' },
    { value: 'CORPORATE', label: 'Corporate', icon: Building2, color: '#3b82f6' },
    { value: 'EVENTS', label: 'Events', icon: PartyPopper, color: '#f59e0b' },
    { value: 'REAL_ESTATE', label: 'Real Estate', icon: Home, color: '#10b981' },
    { value: 'OTHERS', label: 'Other', icon: MoreHorizontal, color: '#8b5cf6' },
  ];

  if (!isOpen) return null;

  // Command Deck dark styling helpers (visual only)
  const cdInput = {
    width: '100%', padding: '11px 13px', border: '1px solid rgba(255,255,255,.14)',
    borderRadius: 11, background: 'rgba(255,255,255,.04)', color: '#fff',
    fontFamily: 'Work Sans, sans-serif', fontSize: '13.5px', outline: 'none'
  };
  const cdSelect = { ...cdInput, appearance: 'none', WebkitAppearance: 'none' };
  const cdFocus = (e) => { e.target.style.borderColor = 'rgba(40,192,208,.55)'; };
  const cdBlur = (e) => { e.target.style.borderColor = 'rgba(255,255,255,.14)'; };
  const cdCard = { background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 18 };
  const cdInnerCard = { background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 14 };
  const cdHeading = { fontFamily: 'Poppins, sans-serif', color: 'rgba(255,255,255,.9)' };
  const cdLabel = { color: 'rgba(255,255,255,.6)', fontFamily: 'Work Sans, sans-serif' };
  const cdSubLabel = { color: 'rgba(255,255,255,.45)' };
  const cdOpt = { background: '#12162A', color: '#fff' };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(8,11,20,.66)', backdropFilter: 'blur(5px)' }}
    >
      <div
        className="w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
        style={{ background: '#12162A', border: '1px solid rgba(255,255,255,.12)', borderRadius: 22, boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)', fontFamily: 'Work Sans, sans-serif' }}
      >

        {/* Header */}
        <div className="p-6 relative" style={{ background: 'linear-gradient(120deg,#572B7C,#78287C)', borderRadius: '22px 22px 0 0' }}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center transition-all"
            style={{ background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.18)', borderRadius: 10 }}
          >
            <X className="w-5 h-5 text-white" />
          </button>

          <div className="flex items-center gap-4">
            <div className="w-14 h-14 flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#572B7C,#28C0D0)', borderRadius: 16 }}>
              <Edit className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl text-white" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800 }}>
                Edit Job
              </h2>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,.7)' }}>
                Modify job details, dates, and crew requirements
              </p>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* Basic Details */}
          <div className="p-6" style={cdCard}>
            <h3 className="text-lg font-bold mb-4" style={cdHeading}>
              Basic Details
            </h3>

            <div className="space-y-4">
              {/* Job Title */}
              <div>
                <label className="block text-sm font-bold mb-2" style={cdLabel}>
                  Job Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  style={cdInput}
                  onFocus={cdFocus}
                  onBlur={cdBlur}
                />
                {errors.title && <p className="text-sm mt-2" style={{ color: '#F87171' }}>{errors.title}</p>}
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-bold mb-2" style={cdLabel}>
                  Event Location *
                </label>

                <div id="edit-job-location-autocomplete" className="mb-3"></div>

                {formData.location && (
                  <div className="flex items-center gap-2 p-3" style={{ background: 'rgba(40,192,208,.08)', border: '1px solid rgba(40,192,208,.22)', borderRadius: 11 }}>
                    <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: '#28C0D0' }} />
                    <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,.85)' }}>
                      {formData.location}
                    </p>
                    {selectedLocation && (
                      <span className="ml-auto text-xs font-semibold" style={{ color: '#28C0D0' }}>
                        ✓ Geocoded
                      </span>
                    )}
                  </div>
                )}
                {errors.location && <p className="text-sm mt-2" style={{ color: '#F87171' }}>{errors.location}</p>}
              </div>
            </div>
          </div>

          {/* Job Type */}
          <div className="p-6" style={cdCard}>
            <h3 className="text-lg font-bold mb-4" style={cdHeading}>
              Job Type
            </h3>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {jobTypeOptions.map(type => {
                const Icon = type.icon;
                const isSelected = formData.jobType === type.value;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, jobType: type.value }))}
                    className="flex flex-col items-center gap-2 p-4 transition-all"
                    style={{
                      borderRadius: 12,
                      border: isSelected ? '2px solid #28C0D0' : '2px solid rgba(255,255,255,.1)',
                      background: isSelected ? 'rgba(40,192,208,.12)' : 'rgba(255,255,255,.03)'
                    }}
                  >
                    <Icon className="w-6 h-6" style={{ color: type.color }} />
                    <span className="text-xs font-bold" style={{ color: isSelected ? '#28C0D0' : 'rgba(255,255,255,.65)' }}>
                      {type.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Event Dates */}
          <div className="p-6" style={cdCard}>
            <h3 className="text-lg font-bold mb-4" style={cdHeading}>
              Event Dates
            </h3>

            <div className="space-y-3">
              {formData.dates.map((date, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="flex-1 relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none" style={{ color: 'rgba(255,255,255,.4)' }} />
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => updateDate(index, e.target.value)}
                      style={{ ...cdInput, paddingLeft: 44, colorScheme: 'dark' }}
                      onFocus={cdFocus}
                      onBlur={cdBlur}
                    />
                  </div>

                  {index === 0 ? (
                    <button
                      type="button"
                      onClick={addDate}
                      className="px-4 py-3 font-bold transition-all"
                      style={{ borderRadius: 11, background: '#28C0D0', color: '#0C1120' }}
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => removeDate(index)}
                      className="w-12 h-12 transition-all flex items-center justify-center"
                      style={{ borderRadius: 11, background: 'rgba(248,113,113,.14)', color: '#F87171' }}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {errors.dates && <p className="text-sm mt-2" style={{ color: '#F87171' }}>{errors.dates}</p>}
          </div>

          {/* Crew Count */}
          <div className="p-6" style={cdCard}>
            <h3 className="text-lg font-bold mb-4" style={cdHeading}>
              Number of Crew Members
            </h3>

            <div className="flex gap-3">
              {[1, 2, 3, 4, 5].map(count => (
                <button
                  key={count}
                  type="button"
                  onClick={() => handleCrewCountChange(count)}
                  className="w-12 h-12 font-bold transition-all"
                  style={{
                    borderRadius: 11,
                    background: formData.crewCount === count ? '#28C0D0' : 'rgba(255,255,255,.06)',
                    color: formData.crewCount === count ? '#0C1120' : 'rgba(255,255,255,.7)'
                  }}
                >
                  {count}
                </button>
              ))}
            </div>
          </div>

          {/* Crew Members */}
          <div className="p-6" style={cdCard}>
            <h3 className="text-lg font-bold mb-4" style={cdHeading}>
              Crew Member Details
            </h3>

            <div className="space-y-6">
              {formData.crewMembers.map((member, memberIndex) => (
                <div key={member.id} className="p-5" style={cdInnerCard}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 flex items-center justify-center text-white font-bold text-sm" style={{ borderRadius: 10, background: 'linear-gradient(135deg,#572B7C,#28C0D0)' }}>
                      {memberIndex + 1}
                    </div>
                    <h4 className="text-base font-bold" style={{ color: '#fff' }}>
                      Crew Member {memberIndex + 1}
                      {member.hired && member.hired_name && (
                        <span className="ml-2 text-xs font-normal" style={{ color: 'rgba(255,255,255,.5)' }}>
                          — {member.hired_name}
                        </span>
                      )}
                    </h4>
                    {!ownerMode && member.hired && member.hired_user_id && (
                      <button
                        type="button"
                        onClick={() => setCancelTarget({
                          job,
                          crewMemberIndex: memberIndex,
                          crewMember: {
                            userId: member.hired_user_id,
                            name: member.hired_name,
                            email: member.hired_email,
                            role: member.role,
                            subtotal: member.subtotal,
                            photo: member.hired_photo
                          }
                        })}
                        className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold transition-all"
                        style={{ borderRadius: 9, border: '1px solid rgba(248,113,113,.4)', color: '#F87171' }}
                        title="Cancel this position (no-show)"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        Cancel (No-Show)
                      </button>
                    )}
                  </div>

                  {/* Role Selector */}
                  <div className="mb-4">
                    <label className="block text-sm font-bold mb-2" style={cdLabel}>
                      Role *
                    </label>
                    <select
                      value={member.role}
                      onChange={(e) => handleCrewMemberChange(memberIndex, 'role', e.target.value)}
                      style={cdSelect}
                      onFocus={cdFocus}
                      onBlur={cdBlur}
                    >
                      <option value="" style={cdOpt}>Select a role...</option>
                      {roleOptions.map(option => (
                        <option key={option.value} value={option.value} style={cdOpt}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Per-Date Assignments */}
                  <div>
                    <label className="block text-sm font-bold mb-3" style={cdLabel}>
                      Hours & Rate Per Date *
                    </label>
                    {formData.dates.map((date, dateIndex) => {
                      const assignment = member.dateAssignments.find(a => a.dateIndex === dateIndex);
                      const isRemoved = !assignment;

                      if (isRemoved) {
                        return (
                          <div key={dateIndex} className="p-3 mb-3 flex items-center justify-between" style={{ borderRadius: 11, border: '2px dashed rgba(255,255,255,.12)', background: 'rgba(255,255,255,.02)' }}>
                            <p className="text-sm" style={{ color: 'rgba(255,255,255,.4)' }}>
                              {date ? formatDateDisplay(date) : `Date ${dateIndex + 1}`} — <span className="italic">not assigned</span>
                            </p>
                            <button
                              type="button"
                              onClick={() => addDateAssignmentForMember(memberIndex, dateIndex)}
                              className="px-3 py-1.5 text-xs font-bold transition-all"
                              style={{ borderRadius: 9, background: 'rgba(40,192,208,.14)', color: '#28C0D0' }}
                            >
                              <Plus className="w-3 h-3 inline mr-1" />
                              Add
                            </button>
                          </div>
                        );
                      }

                      return (
                        <div key={dateIndex} className="p-3 mb-3" style={{ borderRadius: 11, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.08)' }}>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-bold" style={{ color: 'rgba(255,255,255,.75)' }}>
                              {date ? formatDateDisplay(date) : `Date ${dateIndex + 1}`}
                            </p>
                            {member.dateAssignments.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeDateAssignmentForMember(memberIndex, dateIndex)}
                                className="w-7 h-7 flex items-center justify-center transition-all"
                                style={{ borderRadius: 9, background: 'rgba(248,113,113,.14)', color: '#F87171' }}
                                title={`Remove ${date ? formatDateDisplay(date) : 'this date'} for this crew member`}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-bold mb-1" style={cdSubLabel}>
                                Hours
                              </label>
                              <input
                                type="number"
                                value={assignment.hours}
                                onChange={(e) => handleDateAssignmentChange(memberIndex, dateIndex, 'hours', e.target.value)}
                                placeholder="8"
                                min="0"
                                step="any"
                                className="text-xl font-black text-center"
                                style={{ ...cdInput, padding: '11px', color: '#fff' }}
                                onFocus={cdFocus}
                                onBlur={cdBlur}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold mb-1" style={cdSubLabel}>
                                Rate/Hr ($)
                              </label>
                              <input
                                type="number"
                                value={assignment.ratePerHour}
                                onChange={(e) => handleDateAssignmentChange(memberIndex, dateIndex, 'ratePerHour', e.target.value)}
                                placeholder="100"
                                min="0"
                                step="any"
                                className="text-xl font-black text-center"
                                style={{ ...cdInput, padding: '11px', color: '#fff' }}
                                onFocus={cdFocus}
                                onBlur={cdBlur}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {errors.crewMembers && <p className="text-sm mt-2" style={{ color: '#F87171' }}>{errors.crewMembers}</p>}
          </div>

          {/* Job Description */}
          <div className="p-6" style={cdCard}>
            <h3 className="text-lg font-bold mb-4" style={cdHeading}>
              Job Description
            </h3>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows="6"
              style={{ ...cdInput, resize: 'none', lineHeight: 1.5 }}
              onFocus={cdFocus}
              onBlur={cdBlur}
              placeholder="Describe your requirements, expectations, deliverables..."
            />
            {errors.description && <p className="text-sm mt-2" style={{ color: '#F87171' }}>{errors.description}</p>}
          </div>

          {/* WWCC Checkbox */}
          <div className="p-6" style={cdCard}>
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="wwcc-edit"
                checked={formData.requiresWWCC}
                onChange={(e) => setFormData(prev => ({ ...prev, requiresWWCC: e.target.checked }))}
                className="w-5 h-5 mt-0.5"
                style={{ accentColor: '#28C0D0' }}
              />
              <label htmlFor="wwcc-edit" className="flex-1 cursor-pointer">
                <p className="font-bold" style={{ color: '#fff' }}>
                  Working with Children Check Required
                </p>
                <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,.5)' }}>
                  Event involves minors and crew must have valid WWCC
                </p>
              </label>
            </div>
          </div>

          {/* Admin Notes */}
          {!ownerMode && (
          <div className="p-6" style={cdCard}>
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={cdHeading}>
              <FileText className="w-5 h-5" style={{ color: 'rgba(255,255,255,.5)' }} />
              Admin Notes (Internal)
            </h3>
            <textarea
              value={formData.adminNotes}
              onChange={(e) => setFormData(prev => ({ ...prev, adminNotes: e.target.value }))}
              rows="3"
              style={{ ...cdInput, resize: 'none', lineHeight: 1.5 }}
              onFocus={cdFocus}
              onBlur={cdBlur}
              placeholder="Internal notes visible only to admins..."
            />
          </div>
          )}

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-4 font-bold transition-all"
              style={{ borderRadius: 12, background: 'transparent', border: '1px solid rgba(255,255,255,.2)', color: '#fff' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-4 font-bold disabled:opacity-50 transition-all flex items-center justify-center gap-2"
              style={{ borderRadius: 12, background: '#28C0D0', color: '#0C1120' }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Edit className="w-5 h-5" />
                  Update Job
                </>
              )}
            </button>
          </div>
        </form>
        <p className="text-[8px] text-center py-1 opacity-20 select-none">modal:advanced-edit-job</p>
      </div>

      {cancelTarget && (
        <CancelCrewMemberModal
          job={cancelTarget.job}
          crewMember={cancelTarget.crewMember}
          crewMemberIndex={cancelTarget.crewMemberIndex}
          userEmail={adminEmail}
          isOpen={!!cancelTarget}
          onClose={() => setCancelTarget(null)}
          onSuccess={() => {
            setCancelTarget(null);
            if (onUpdate) onUpdate();
            onClose();
          }}
          adminMode
        />
      )}
    </div>
  );
};

export default AdvancedEditJobModal;